import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const winLossAnalysisArticle: BlogArticle = {
  key: BlogArticleKey.WIN_LOSS_ANALYSIS,
  slug: BlogSlugs.WIN_LOSS_ANALYSIS,
  categoryId: BlogCategory.WIN_LOSS,
  date: "2026-09-29",
  readTime: 9,
  locales: {
    fr: {
      title: "Analyse win/loss : comprendre vos deals à partir des appels",
      excerpt:
        "Le motif de perte saisi dans le CRM raconte ce que le commercial a retenu, pas ce que l'acheteur a dit. Voici une méthode pour analyser vos deals gagnés et perdus à partir des échanges eux-mêmes.",
      description:
        "Une méthode d'analyse win/loss fondée sur ce que les acheteurs ont dit en appel : quoi relever par deal, comment comparer, quelles questions poser.",
      ctaTitle: "Sachez pourquoi vos deals se gagnent et se perdent",
      ctaText:
        "On repère dans vos échanges les deals ouverts, gagnés et perdus, les objections, les concurrents cités et les critères de décision, et on les rend comptables, passage à l'appui.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Un deal perdu se referme presque toujours de la même façon. Le commercial ouvre le CRM, choisit un motif dans une liste déroulante, souvent « Prix » ou « Pas le bon moment », et passe au suivant. Six mois plus tard, le rapport des motifs de perte montre que le prix est la première cause, et personne ne sait quoi en faire.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce rapport n'est pas faux, il est incomplet. Il mesure ce que l'équipe a retenu d'un deal, pas ce que l'acheteur a réellement dit pendant les appels. Et entre les deux, il y a précisément ce qui vous permettrait de gagner le suivant.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Pourquoi le motif de perte du CRM ne suffit pas",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le champ « motif de perte » a trois défauts qui ne tiennent pas à la bonne volonté des commerciaux, mais à la façon dont il est rempli.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Il est rempli après coup, de mémoire.** Le commercial résume en un mot un cycle de plusieurs semaines et de plusieurs interlocuteurs. Ce qui reste est l'impression de la fin, rarement la cause du début.",
            "**Il est rempli par une partie prenante.** Attribuer une perte au prix ou au calendrier ne met personne en cause. Attribuer une perte à une découverte trop rapide ou à un décideur jamais rencontré, si. Le biais n'est pas malhonnête, il est humain.",
            "**Il ne garde pas la matière.** Une liste déroulante enregistre une catégorie, jamais la phrase qui la justifie. Impossible ensuite de vérifier, de nuancer ou de comparer.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "« Trop cher » est rarement une cause. C'est le plus souvent la conclusion d'un acheteur à qui la valeur n'a pas été démontrée.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La bonne nouvelle, c'est que la matière existe déjà. Elle est dans les appels de découverte, les démos, les négociations. L'acheteur y a dit ce qui déclenchait son besoin, ce qu'il comparait, qui devait valider et ce qui l'inquiétait. Une analyse win/loss sérieuse commence par relire cette matière plutôt que la mémoire.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce qu'il faut relever pour chaque deal",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Pour que deux deals se comparent, il faut relever les mêmes éléments sur chacun. Six suffisent, à condition de les relever à partir de ce qui a été dit, et de garder la phrase qui les justifie.",
        },
        {
          type: ArticleBlockType.H3,
          text: "1. Le moment où le besoin est apparu",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Qu'est-ce qui a fait que l'acheteur a commencé à chercher maintenant ? Un recrutement, un départ, un audit, un incident, un nouveau dirigeant. Un besoin déclenché par un événement daté avance ; un besoin « qu'on a depuis longtemps » attend souvent encore.",
        },
        {
          type: ArticleBlockType.H3,
          text: "2. Les critères de décision",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce sur quoi l'acheteur a dit qu'il jugerait les solutions, dans ses mots. Par exemple, à titre d'illustration : « Il faut que ce soit en place avant la saison haute » ou « Notre DSI ne validera rien qui ne soit pas hébergé en Europe ». Un critère exprimé et jamais traité dans la démo est une perte annoncée.",
        },
        {
          type: ArticleBlockType.H3,
          text: "3. Qui décide",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les personnes citées dans le processus de décision, et celles que vous avez réellement rencontrées. L'écart entre les deux listes explique à lui seul une bonne part des deals qui s'arrêtent sans raison apparente.",
        },
        {
          type: ArticleBlockType.H3,
          text: "4. Les concurrents mentionnés",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Qui d'autre était regardé, et à quel moment le nom est apparu. Un concurrent cité dès la découverte n'a pas le même poids qu'un nom qui surgit la semaine de la décision. N'oubliez pas le concurrent le plus fréquent : le statu quo, le tableur, l'outil maison.",
        },
        {
          type: ArticleBlockType.H3,
          text: "5. Les objections, et si elles ont été traitées",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Chaque objection soulevée, puis la réponse apportée, et surtout la réaction de l'acheteur à cette réponse. Une objection « traitée » dont l'acheteur reparle deux appels plus tard ne l'était pas.",
        },
        {
          type: ArticleBlockType.H3,
          text: "6. La dernière étape convenue",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce qui a été décidé à la fin du dernier échange, avec une date et un responsable, ou l'absence de tout cela. Un deal qui se termine sur « on revient vers vous » s'est souvent perdu à ce moment précis, bien avant le refus officiel.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Lire les deals gagnés et perdus côte à côte",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'erreur la plus courante est d'analyser seulement les pertes. Sans les gains à côté, vous ne savez pas si un élément est propre aux deals perdus ou simplement présent partout. Une objection sur le prix entendue dans les deals gagnés aussi n'explique pas vos pertes.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que vous observez", "Ce que ça indique"],
          rows: [
            [
              "Le même critère exprimé dans les gains et les pertes, traité seulement dans les gains",
              "Un défaut de démo ou de discours, pas de produit",
            ],
            [
              "Un concurrent cité surtout dans les pertes",
              "Un argumentaire comparatif à construire, ou une cible où il est plus fort",
            ],
            [
              "Un décideur rencontré dans les gains, jamais dans les pertes",
              "Un problème de qualification et d'accès, pas de conviction",
            ],
            [
              "Des pertes sans déclencheur daté",
              "Des deals ouverts trop tôt, qui gonflent le pipeline",
            ],
            [
              "Des pertes qui se terminent sans étape suivante datée",
              "Une fin d'appel à reprendre, plus qu'un problème de fond",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ce qui compte ici n'est pas un deal, c'est la récurrence. Un élément présent dans une perte est une anecdote. Le même élément présent dans la majorité des pertes et absent des gains est une piste de travail.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce que dit le CRM, et ce que disent les appels",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le motif saisi dans le CRM",
            items: [
              "Perdu : prix",
              "Perdu : pas le bon moment",
              "Perdu : concurrent",
              "Perdu : sans réponse",
            ],
          },
          good: {
            title: "Ce que l'acheteur a dit",
            items: [
              "« Je ne vois pas encore comment le justifier auprès de ma direction »",
              "« Tant que le nouveau responsable n'est pas arrivé, je ne peux rien engager »",
              "« L'autre solution s'intègre déjà à notre outil de facturation »",
              "Aucune étape convenue après la démo, le DAF jamais rencontré",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les phrases de droite sont des exemples, mais elles montrent l'écart. « Prix » devient un problème de justification interne, donc un argument de retour sur investissement à outiller. « Concurrent » devient une intégration manquante. « Sans réponse » devient une qualification incomplète.",
        },
        {
          type: ArticleBlockType.H2,
          text: "L'entretien win/loss avec un client perdu",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les appels racontent le cycle vu de l'extérieur. Un entretien court avec l'acheteur après la décision complète ce qu'ils ne disent pas. Il fonctionne mieux s'il est mené par quelqu'un d'autre que le commercial du deal, quelques semaines après la décision, en vingt minutes au plus, et en annonçant clairement qu'il ne s'agit pas de rouvrir la vente.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Qu'est-ce qui vous a amené à chercher une solution à ce moment-là ?",
            "Quelles options avez-vous regardées, y compris ne rien changer ?",
            "Sur quels critères avez-vous finalement tranché ?",
            "Qui a participé à la décision, et qui avait le dernier mot ?",
            "À quel moment avez-vous senti que votre choix était fait ?",
            "Qu'est-ce qui vous a manqué de notre côté pour aller plus loin ?",
            "Y a-t-il une question à laquelle nous n'avons jamais vraiment répondu ?",
            "Si vous deviez refaire ce choix aujourd'hui, que regarderiez-vous en premier ?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Écoutez sans corriger. Le but n'est pas d'avoir raison sur le deal, c'est de comprendre comment l'acheteur l'a vécu. Enregistrez l'entretien avec son accord et rattachez-le au même client que les autres échanges, pour qu'il soit relu avec eux.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Une revue mensuelle, pas un projet annuel",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une analyse win/loss menée une fois par an produit un beau document et peu de changements. Une revue mensuelle courte produit l'inverse. Une heure suffit, avec le responsable commercial et deux ou trois commerciaux, sur les deals clos du mois.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Lister les deals gagnés et perdus du mois, sans exception.",
            "Pour chacun, relever les six éléments à partir des échanges, pas de la mémoire.",
            "Chercher ce qui revient dans les pertes et manque dans les gains.",
            "Garder une ou deux phrases d'acheteur par constat, pour que la discussion porte sur des faits.",
            "Décider d'un seul changement pour le mois suivant, et le nommer.",
            "Le mois d'après, commencer par vérifier si ce changement a été appliqué.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Sur un petit volume de deals, soyez prudent : quelques cas ne font pas une tendance. Cumulez sur plusieurs mois avant de tirer une conclusion forte, et gardez l'habitude de revenir aux passages plutôt qu'à des pourcentages.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Ce qu'il faut changer ensuite",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une analyse win/loss ne sert que si elle modifie quelque chose dans la façon de vendre. Trois endroits concentrent la plupart des corrections.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Les questions de découverte.** Si les pertes manquent de déclencheur daté ou de critères explicites, ajoutez les questions qui les font émerger dès le premier appel.",
            "**La qualification.** Si les pertes n'ont jamais atteint le décideur, faites de sa rencontre une condition pour avancer le deal, et non un espoir pour la fin du cycle.",
            "**Le discours et la démo.** Si un critère ou un concurrent revient dans les pertes, construisez la réponse, placez-la dans la démo et vérifiez le mois suivant qu'elle est utilisée.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Tenir ce travail sans tout relire à la main",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Relever six éléments sur chaque deal à partir des appels demande du temps si tout se fait à la main. C'est là qu'on peut aider. Chaque échange rattaché à une fiche client est transcrit puis analysé : les objections, les objections traitées, les mentions de concurrents, les critères de décision, le processus de décision et les prochaines étapes y sont repérés comme des passages, avec les mots de l'acheteur.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les deals sont détectés à partir de ce qui a été dit, ouverts, gagnés ou perdus, et restent modifiables à la main quand l'équipe sait mieux. L'assistant peut ensuite répondre à une question comme « quelles objections reviennent le plus dans les deals perdus ce trimestre ? » en comptant les thèmes à partir des passages repérés, chaque affirmation renvoyant au passage exact.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Posez la question à vos échanges",
          text: "Demandez à l'assistant de Meidly ce qui revient dans vos deals perdus, les concurrents cités ou les critères de décision exprimés. Il compte les thèmes et cite les passages sur lesquels il s'appuie.",
          linkLabel: "Découvrir l'assistant",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'outil ne remplace pas la revue : il en prépare la matière. La décision de changer une question de découverte ou une étape de qualification reste celle de l'équipe, et elle se prend mieux quand chacun peut relire ce que les acheteurs ont vraiment dit.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un deal perdu coûte cher une fois. Un deal perdu pour une raison que personne n'a comprise coûte cher à chaque répétition.",
        },
      ],
      faq: [
        {
          question: "Qu'est-ce qu'une analyse win/loss ?",
          answer:
            "C'est l'étude des deals gagnés et perdus pour comprendre ce qui fait la différence entre les deux. Menée à partir des échanges avec les acheteurs plutôt que du motif saisi dans le CRM, elle révèle les critères, les objections et les étapes qui décident réellement de l'issue.",
        },
        {
          question: "Pourquoi ne pas se contenter du motif de perte du CRM ?",
          answer:
            "Parce qu'il est rempli après coup, de mémoire, par une partie prenante, et qu'il ne garde qu'une catégorie. Il est utile comme indicateur, mais il ne dit ni ce que l'acheteur a exprimé, ni ce qui aurait pu changer la décision.",
        },
        {
          question: "Faut-il aussi analyser les deals gagnés ?",
          answer:
            "Oui. Sans les gains, vous ne savez pas si un élément est propre aux pertes ou présent partout. C'est la comparaison entre les deux qui fait apparaître ce qui décide vraiment.",
        },
        {
          question: "Qui doit mener l'entretien avec un client perdu ?",
          answer:
            "De préférence quelqu'un d'autre que le commercial du deal, pour que l'acheteur parle librement. L'entretien est court, annoncé comme tel, et ne cherche jamais à rouvrir la vente.",
        },
        {
          question: "À quelle fréquence faire une revue win/loss ?",
          answer:
            "Une revue mensuelle d'une heure sur les deals clos du mois fonctionne mieux qu'une grande analyse annuelle. Sur un petit volume, cumulez plusieurs mois avant de tirer des conclusions fortes.",
        },
      ],
    },
    en: {
      title: "Win/loss analysis: understanding deals from your sales calls",
      excerpt:
        "The lost reason entered in the CRM tells you what the rep remembered, not what the buyer said. Here is a method to analyse won and lost deals from the conversations themselves.",
      description:
        "A win/loss analysis method built on what buyers said on calls: what to capture per deal, how to compare wins and losses, which questions to ask.",
      ctaTitle: "Know why your deals are won and lost",
      ctaText:
        "We spot the deals opened, won and lost in your conversations, along with objections, competitors mentioned and decision criteria, and make them countable, with the passage to back each one.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'A lost deal is almost always closed the same way. The rep opens the CRM, picks a reason from a dropdown, often "Price" or "Bad timing", and moves on. Six months later the lost reason report shows price as the top cause, and nobody knows what to do with it.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That report is not wrong, it is incomplete. It measures what the team remembered about a deal, not what the buyer actually said during the calls. And in between lies exactly what would help you win the next one.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Why the CRM lost reason is not enough",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The "lost reason" field has three flaws that have nothing to do with the reps\' good faith and everything to do with how it gets filled in.',
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**It is filled in afterwards, from memory.** The rep sums up in one word a cycle of several weeks and several people. What remains is the impression of the end, rarely the cause at the start.",
            "**It is filled in by an interested party.** Putting a loss down to price or timing blames no one. Putting it down to a rushed discovery or a decision maker never met does. The bias is not dishonest, it is human.",
            "**It keeps none of the material.** A dropdown records a category, never the sentence behind it. You cannot check, qualify or compare it afterwards.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: '"Too expensive" is rarely a cause. Most of the time it is the conclusion of a buyer to whom the value was never shown.',
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The good news is that the material already exists. It sits in the discovery calls, the demos, the negotiations. The buyer said there what triggered the need, what they were comparing, who had to sign off and what worried them. A serious win/loss analysis starts by rereading that material rather than memory.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What to capture for each deal",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "For two deals to be comparable, you need to capture the same elements on each. Six are enough, as long as they come from what was said and you keep the sentence that supports them.",
        },
        {
          type: ArticleBlockType.H3,
          text: "1. The moment the need appeared",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'What made the buyer start looking now? A hire, a departure, an audit, an incident, a new executive. A need triggered by a dated event moves forward; a need "we have had for ages" often keeps waiting.',
        },
        {
          type: ArticleBlockType.H3,
          text: "2. The decision criteria",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'What the buyer said they would judge solutions on, in their own words. For instance, as an illustration: "It has to be live before our peak season" or "Our IT will not approve anything not hosted in Europe". A criterion voiced and never addressed in the demo is a loss foretold.',
        },
        {
          type: ArticleBlockType.H3,
          text: "3. Who decides",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The people named in the decision process, and the ones you actually met. The gap between those two lists alone explains a good share of the deals that stall for no apparent reason.",
        },
        {
          type: ArticleBlockType.H3,
          text: "4. The competitors mentioned",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Who else was being looked at, and when the name came up. A competitor named in discovery does not weigh the same as a name that surfaces in the week of the decision. Do not forget the most frequent competitor: the status quo, the spreadsheet, the in-house tool.",
        },
        {
          type: ArticleBlockType.H3,
          text: "5. The objections, and whether they were handled",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Each objection raised, the answer given, and above all how the buyer reacted to that answer. A "handled" objection the buyer brings up again two calls later was not handled.',
        },
        {
          type: ArticleBlockType.H3,
          text: "6. The last next step agreed",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'What was decided at the end of the last conversation, with a date and an owner, or the absence of all that. A deal that ends on "we will get back to you" was often lost right there, well before the official no.',
        },
        {
          type: ArticleBlockType.H2,
          text: "Reading won and lost deals side by side",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The most common mistake is to analyse only the losses. Without the wins next to them, you cannot tell whether an element is specific to lost deals or simply everywhere. A price objection also heard in the deals you won does not explain your losses.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What you observe", "What it points to"],
          rows: [
            [
              "The same criterion voiced in wins and losses, addressed only in the wins",
              "A demo or messaging gap, not a product gap",
            ],
            [
              "A competitor named mostly in the losses",
              "A comparison argument to build, or a segment where they are stronger",
            ],
            [
              "A decision maker met in the wins, never in the losses",
              "A qualification and access problem, not a conviction problem",
            ],
            ["Losses with no dated trigger", "Deals opened too early, inflating the pipeline"],
            [
              "Losses that end with no dated next step",
              "A call ending to rework, more than a deeper problem",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "What matters here is not one deal, it is recurrence. An element present in one loss is an anecdote. The same element present in most losses and missing from the wins is something to work on.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What the CRM says, and what the calls say",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The reason entered in the CRM",
            items: ["Lost: price", "Lost: bad timing", "Lost: competitor", "Lost: no response"],
          },
          good: {
            title: "What the buyer said",
            items: [
              '"I still do not see how to justify it to my management"',
              '"Until the new manager starts, I cannot commit to anything"',
              '"The other solution already connects to our invoicing tool"',
              "No next step agreed after the demo, the CFO never met",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'The sentences on the right are examples, but they show the gap. "Price" becomes an internal justification problem, so a return on investment argument to equip. "Competitor" becomes a missing integration. "No response" becomes incomplete qualification.',
        },
        {
          type: ArticleBlockType.H2,
          text: "The win/loss interview with a lost customer",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The calls tell the cycle from the outside. A short interview with the buyer after the decision fills in what they leave out. It works better when run by someone other than the deal's rep, a few weeks after the decision, in twenty minutes at most, and with a clear statement that it is not about reopening the sale.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "What led you to look for a solution at that point?",
            "Which options did you consider, including changing nothing?",
            "Which criteria did you finally decide on?",
            "Who took part in the decision, and who had the final say?",
            "At what point did you feel your choice was made?",
            "What was missing on our side for you to go further?",
            "Is there a question we never really answered?",
            "If you had to make this choice again today, what would you look at first?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Listen without correcting. The goal is not to be right about the deal, it is to understand how the buyer experienced it. Record the interview with their consent and attach it to the same customer as the other conversations, so it is reread alongside them.",
        },
        {
          type: ArticleBlockType.H2,
          text: "A monthly review, not a yearly project",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A win/loss analysis run once a year produces a fine document and few changes. A short monthly review produces the opposite. One hour is enough, with the sales lead and two or three reps, on the deals closed that month.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "List the deals won and lost this month, without exception.",
            "For each, capture the six elements from the conversations, not from memory.",
            "Look for what comes back in the losses and is missing from the wins.",
            "Keep one or two buyer sentences per finding, so the discussion rests on facts.",
            "Decide on a single change for the next month, and name it.",
            "The following month, start by checking whether that change was applied.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "On a small number of deals, be careful: a handful of cases is not a trend. Add up several months before drawing a strong conclusion, and keep going back to the passages rather than to percentages.",
        },
        {
          type: ArticleBlockType.H2,
          text: "What to change next",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A win/loss analysis is only useful if it changes something in the way you sell. Three places hold most of the corrections.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Discovery questions.** If the losses lack a dated trigger or explicit criteria, add the questions that bring them out from the first call.",
            "**Qualification.** If the losses never reached the decision maker, make meeting them a condition for moving the deal forward, not a hope for the end of the cycle.",
            "**Messaging and demo.** If a criterion or a competitor keeps coming back in the losses, build the answer, place it in the demo and check the following month that it is being used.",
          ],
        },
        {
          type: ArticleBlockType.H2,
          text: "Keeping this up without rereading everything by hand",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Capturing six elements per deal from the calls takes time if it is all done by hand. That is where we can help. Each conversation attached to a customer file is transcribed then analysed: objections, handled objections, competitor mentions, decision criteria, decision process and next steps are picked out as passages, in the buyer's words.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: 'Deals are detected from what was said, opened, won or lost, and stay editable by hand when the team knows better. The assistant can then answer a question such as "which objections come up most in the deals we lost this quarter?" by counting themes from the tagged passages, each claim pointing to the exact passage.',
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Ask your conversations directly",
          text: "Ask Meidly's assistant what keeps coming back in your lost deals, which competitors are named or which decision criteria are voiced. It counts the themes and cites the passages it relies on.",
          linkLabel: "Discover the assistant",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The tool does not replace the review, it prepares its material. The decision to change a discovery question or a qualification step stays with the team, and it is easier to make when everyone can reread what buyers actually said.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A lost deal costs you once. A deal lost for a reason nobody understood costs you every time it happens again.",
        },
      ],
      faq: [
        {
          question: "What is a win/loss analysis?",
          answer:
            "It is the study of won and lost deals to understand what separates them. Run from the conversations with buyers rather than from the reason entered in the CRM, it reveals the criteria, objections and steps that actually decide the outcome.",
        },
        {
          question: "Why not rely on the CRM lost reason?",
          answer:
            "Because it is filled in afterwards, from memory, by an interested party, and it keeps only a category. It is useful as an indicator, but it tells you neither what the buyer expressed nor what could have changed the decision.",
        },
        {
          question: "Should won deals be analysed too?",
          answer:
            "Yes. Without the wins, you cannot tell whether an element is specific to the losses or present everywhere. Comparing the two is what reveals what really decides.",
        },
        {
          question: "Who should run the interview with a lost customer?",
          answer:
            "Preferably someone other than the deal's rep, so the buyer speaks freely. The interview is short, announced as such, and never tries to reopen the sale.",
        },
        {
          question: "How often should a win/loss review happen?",
          answer:
            "A one hour monthly review of the deals closed that month works better than a large yearly analysis. On a small volume, add up several months before drawing strong conclusions.",
        },
      ],
    },
  },
};
